import React, { useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Tooltip, Filler, TimeSeriesScale,
} from 'chart.js';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Filler, TimeSeriesScale);

// Validated categorical order (see dataviz palette). Color follows the entity.
const TRANSPORT_COLORS_DARK = { wifi: '#3987e5', '4g': '#199e70', auto: '#c98500', uart: '#9085e9', bluetooth: '#e66767', unknown: '#64748b' };
const TRANSPORT_COLORS_LIGHT = { wifi: '#2a78d6', '4g': '#1baf7a', auto: '#eda100', uart: '#4a3aa7', bluetooth: '#e34948', unknown: '#94a3b8' };

const baseOpts = (theme) => {
  const grid = theme.palette.divider;
  const tick = theme.palette.text.secondary;
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#0f172a',
        titleColor: '#fff', bodyColor: '#e2e8f0', padding: 10, cornerRadius: 8, displayColors: false,
      },
    },
    scales: {
      x: { grid: { display: false }, border: { color: grid }, ticks: { color: tick, font: { size: 11 } } },
      y: { grid: { color: grid, drawBorder: false }, border: { display: false }, ticks: { color: tick, font: { size: 11 }, precision: 0, maxTicksLimit: 5 }, beginAtZero: true },
    },
  };
};

export const TestsOverTime = ({ series = [] }) => {
  const theme = useTheme();
  const brand = theme.palette.primary.main;
  const data = useMemo(() => ({
    labels: series.map((s) => dayjs(s.day).format('MMM D')),
    datasets: [{
      data: series.map((s) => s.count),
      borderColor: brand,
      backgroundColor: (ctx) => {
        const { chartArea, ctx: c } = ctx.chart;
        if (!chartArea) return 'transparent';
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, brand + '40'); g.addColorStop(1, brand + '00');
        return g;
      },
      borderWidth: 2, fill: true, tension: 0.35,
      pointRadius: 0, pointHoverRadius: 5, pointHoverBackgroundColor: brand,
    }],
  }), [series, brand]);
  const opts = useMemo(() => {
    const o = baseOpts(theme);
    o.plugins.tooltip.callbacks = { title: (i) => i[0].label, label: (i) => ` ${i.parsed.y} tests` };
    o.interaction = { mode: 'index', intersect: false };
    return o;
  }, [theme]);
  return <Line data={data} options={opts} />;
};

export const TransportMix = ({ transport = [] }) => {
  const theme = useTheme();
  const colors = theme.palette.mode === 'dark' ? TRANSPORT_COLORS_DARK : TRANSPORT_COLORS_LIGHT;
  // Hide the legacy "unknown" bucket (older logs recorded before transport_mode existed).
  const rows = transport.filter((r) => r.mode !== 'unknown').sort((a, b) => b.count - a.count);
  const data = useMemo(() => ({
    labels: rows.map((r) => (r.mode === '4g' ? '4G' : r.mode.charAt(0).toUpperCase() + r.mode.slice(1))),
    datasets: [{
      data: rows.map((r) => r.count),
      backgroundColor: rows.map((r) => colors[r.mode] || colors.unknown),
      borderRadius: 5, borderSkipped: false, barThickness: 18,
    }],
  }), [rows, colors]);
  const opts = useMemo(() => {
    const o = baseOpts(theme);
    o.indexAxis = 'y';
    o.scales.x.grid = { color: theme.palette.divider, drawBorder: false };
    o.scales.x.ticks.precision = 0;
    o.scales.y.grid = { display: false };
    o.plugins.tooltip.callbacks = { label: (i) => ` ${i.parsed.x} commands` };
    return o;
  }, [theme]);
  return <Bar data={data} options={opts} />;
};
